import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useLocation, useNavigate} from "react-router-dom";
import {calculateRentPriceAPI} from "../../network/publicCalls/GETCalculateRentPrice";
import {customerCanCreateReservation, formatPriceNl} from "../../config/toolKit";
import {createPaymentAPI} from "../../network/loggedInCalls/POSTCreatePayment";
import {createReservationAPI} from "../../network/loggedInCalls/POSTCreateReservation";
import CheqmAddressModal from "./CheqmAddressModal";
import {createP2PReservationRequestAPI} from "../../network/loggedInCalls/POSTCreateP2PReservationRequest";
import {getCustomersAPI} from "../../network/loggedInCalls/GETCustomers";
import {getCustomerTransactionDetailAPI} from "../../network/loggedInCalls/GETCustomerTransactionDetail";
import {getTariffName, isHolidayTariff, isSaturdayTariff, isSpecialTariffType, isWeekendTariff} from "../../config/categoryTariffs";
import {resolveWhiteLabelSubsidiaryId} from "../../config/subsidiaryId";
import {getAppliedWhiteLabelThemeId} from "../../config/whiteLabelThemes";
import {appendPublicWrapperBaseToSearch} from "../../config/publicWrapperBase";
import {sendEvent} from "../../config/analytics";
import {buildUnavailableMessage, resolveBookableVehicle, unavailableMessageToText} from "../../config/locationAvailability";

const formatDateDisplay = (value) => {
    if (!value) return "--";
    const [datePart] = value.split("T");
    if (!datePart) return "--";
    const [year, month, day] = datePart.split("-");
    if (!year || !month || !day) return "--";
    return `${day}-${month}-${year}`;
};

const formatTimeDisplay = (value) => {
    if (!value) return "--";
    const parts = value.includes("T") ? value.split("T") : value.split(" ");
    const timePart = parts[1];
    return timePart ? timePart.slice(0, 5) : "--";
};

const getDurationLabel = (fromValue, toValue) => {
    if (!fromValue || !toValue) return "--";
    const start = new Date(fromValue);
    const end = new Date(toValue);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return "--";
    const diffMinutes = Math.max(0, Math.round((end.getTime() - start.getTime()) / 60000));
    if (!diffMinutes) return "--";
    if (diffMinutes < 24 * 60) {
        const hours = Math.max(1, Math.ceil(diffMinutes / 60));
        return `${hours} uur`;
    }
    const days = Math.max(1, Math.ceil(diffMinutes / (24 * 60)));
    return `${days} ${days === 1 ? "huurdag" : "huurdagen"}`;
};

const getReservationIdentifier = (reservation) => {
    if (!reservation) return "";
    return (
        reservation?.reservation_number ||
        reservation?.reservation_request_number ||
        reservation?.reservation_request_id ||
        reservation?.request_number ||
        reservation?.request_id ||
        reservation?.reservationNumber ||
        reservation?.number ||
        reservation?.id ||
        ""
    );
};

const findPreferredCustomer = (customers, customerId) => {
    if (!Array.isArray(customers) || customers.length === 0) return null;
    const byId = customers.find((item) => Number(item?.id) === Number(customerId));
    if (byId) return byId;
    const privateCustomer = customers.find((item) => item?.type === "private");
    return privateCustomer || customers[0] || null;
};

const EMPTY_OPTIONS = Object.freeze([]);
const EMPTY_PRODUCTS = Object.freeze({});

const KAV2GO_COMPANY_ID = 9;

const getAdditionalOrderLineDescription = (line, companyId) => {
    const description = String(line?.description || "");
    const type = String(line?.type || "").toLowerCase();

    if (Number(companyId) !== KAV2GO_COMPANY_ID) return description;

    if (type === "insurance_option") {
        return "Verplichte afkoop eigen risico";
    }
    if (type === "extra_kilometers") {
        return "Kilometers";
    }

    return description;
};

const formatLengthSpec = (dimensions) => {
    if (!dimensions) return null;
    const firstPart = String(dimensions).split("x")[0]?.trim();
    if (!firstPart) return null;

    const parsed = Number(firstPart.replace(",", "."));
    if (!Number.isFinite(parsed)) return `Lengte ${firstPart}`;

    const lengthInCm = parsed <= 10 ? Math.round(parsed * 100) : Math.round(parsed);
    return `Lengte ${lengthInCm} cm`;
};

const formatVolumeSpec = (volume) => {
    if (volume === null || volume === undefined || volume === "") return null;
    const parsed = Number(String(volume).replace(",", "."));
    if (!Number.isFinite(parsed)) return null;
    const normalized = parsed % 1 === 0
        ? String(parsed)
        : parsed.toLocaleString("nl-NL", {minimumFractionDigits: 1, maximumFractionDigits: 1});
    return `${normalized} m³`;
};

const formatWeightSpec = (weight) => {
    if (weight === null || weight === undefined || weight === "") return null;
    const parsed = Number(String(weight).replace(",", "."));
    if (!Number.isFinite(parsed)) return null;
    return `${Math.round(parsed)} kg`;
};

// POSTCreateReservation: ReservationVehicleNotAvailableException (other API errors use 406 too).
const isVehicleNotAvailableError = (error) => (
    Number(error?.status) === 406 && /not available/i.test(String(error?.apiError?.message || ""))
);

const isCheqmRequired = (customer) => Boolean(
    customer && Array.isArray(customer.required_screenings) && customer.required_screenings.includes("cheqm_required")
);

export default function ReservationVehicle({
    vehicleInfo,
    rentalPeriod,
    expectedKm = "",
    selectedOptions = EMPTY_OPTIONS,
    selectedProducts = EMPTY_PRODUCTS,
    optionsReady = true,
    showDeposit = false,
    minRentalText = "",
    showMinRentalWarning = false,
    activeCustomer = null,
    authToken = null,
    locationId = "",
    onPricingMetaChange = null,
    onPriceLoadingChange = null,
    showPricesInclVat = true,
    showLongDailySupportMessage = false,
    demoVirtualCheckout = false,
    hideActionButtons = false,
    onDemoPaymentAmountChange = null,
    onCustomerChanged = null
}) {
    const navigate = useNavigate();
    const location = useLocation();

    const [isInfoOpen, setIsInfoOpen] = useState(false);
    const [isImagePreviewOpen, setIsImagePreviewOpen] = useState(false);

    const [reservationPrice, setReservationPrice] = useState(null);
    const [isPriceLoading, setIsPriceLoading] = useState(true);

    const [runtimeBalance, setRuntimeBalance] = useState(null);

    const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
    const [paymentSession, setPaymentSession] = useState({id: null, url: ""});
    const [paymentStatus, setPaymentStatus] = useState("idle");
    const [paymentError, setPaymentError] = useState("");
    const [paymentErrorType, setPaymentErrorType] = useState("");
    const [isCreatingPaymentLink, setIsCreatingPaymentLink] = useState(false);

    const [isCreatingReservation, setIsCreatingReservation] = useState(false);
    const [reservationError, setReservationError] = useState("");
    const [isCheqmModalOpen, setIsCheqmModalOpen] = useState(false);
    // "rejected" or "failed": the customer checks stopped the reservation, only support can continue
    const [reservationCheckProblem, setReservationCheckProblem] = useState(null);
    const cheqmDoneRef = useRef(false);
    const [isP2PRequestCommentModalOpen, setIsP2PRequestCommentModalOpen] = useState(false);
    const [p2pRequestComment, setP2PRequestComment] = useState("");

    const requestIdRef = useRef(0);
    const paymentPollingIntervalRef = useRef(null);
    const reservationCreationRef = useRef(false);

    const featureItems = [
        {icon: "bi bi-fuel-pump", label: vehicleInfo.model.fuelType},
        {icon: "bi bi-door-closed", label: `${vehicleInfo.model.numberOfDoors} deuren`},
        {icon: "bi bi-people", label: `${vehicleInfo.model.numberOfSeats} personen`},
        {icon: "bi bi-gear", label: vehicleInfo.model.transmission},
        {icon: "bi bi-card-text", label: `Rijbewijs (${vehicleInfo.model.driversLicense})`},
    ];
    const loadingSpaceSpecs = useMemo(() => {
        const loadingSpace = vehicleInfo?.model?.loadingSpace || {};
        return [
            formatLengthSpec(loadingSpace.dimensions),
            formatVolumeSpec(loadingSpace.volume),
            formatWeightSpec(loadingSpace.weight),
        ].filter(Boolean);
    }, [vehicleInfo?.model?.loadingSpace]);

    const pickupDate = useMemo(
        () => formatDateDisplay(rentalPeriod?.from),
        [rentalPeriod?.from]
    );
    const pickupTime = useMemo(
        () => formatTimeDisplay(rentalPeriod?.from),
        [rentalPeriod?.from]
    );
    const returnDate = useMemo(
        () => formatDateDisplay(rentalPeriod?.to),
        [rentalPeriod?.to]
    );
    const returnTime = useMemo(
        () => formatTimeDisplay(rentalPeriod?.to),
        [rentalPeriod?.to]
    );
    const rentalDuration = useMemo(
        () => getDurationLabel(rentalPeriod?.from, rentalPeriod?.to),
        [rentalPeriod?.from, rentalPeriod?.to]
    );

    const normalizedOptions = useMemo(
        () => selectedOptions
            .map((option) => Number(option))
            .filter((option) => Number.isFinite(option)),
        [selectedOptions]
    );
    const optionsKey = useMemo(
        () => normalizedOptions.slice().sort((a, b) => a - b).join(","),
        [normalizedOptions]
    );
    const stableOptions = useMemo(() => {
        if (!optionsKey) return EMPTY_OPTIONS;
        return optionsKey
            .split(",")
            .map((option) => Number(option))
            .filter((option) => Number.isFinite(option));
    }, [optionsKey]);
    const normalizedProducts = useMemo(() => {
        return Object.entries(selectedProducts || {}).reduce((carry, [productId, productPayload]) => {
            const parsedProductId = Number(productId);
            const parsedQuantity = Number(
                typeof productPayload === "object" && productPayload !== null
                    ? productPayload.quantity
                    : productPayload
            );
            const parsedDays = Number(
                typeof productPayload === "object" && productPayload !== null
                    ? productPayload.days
                    : NaN
            );
            if (!Number.isFinite(parsedProductId) || parsedProductId <= 0) return carry;
            if (!Number.isFinite(parsedQuantity) || parsedQuantity <= 0) return carry;
            carry[parsedProductId] = {
                quantity: Math.floor(parsedQuantity),
                days: Number.isFinite(parsedDays) && parsedDays > 0 ? Math.floor(parsedDays) : null
            };
            return carry;
        }, {});
    }, [selectedProducts]);
    const productsKey = useMemo(() => {
        return Object.entries(normalizedProducts)
            .sort(([a], [b]) => Number(a) - Number(b))
            .map(([productId, payload]) => {
                const quantity = Number(payload?.quantity);
                const days = Number(payload?.days);
                if (!Number.isFinite(quantity) || quantity <= 0) return null;
                if (Number.isFinite(days) && days > 0) {
                    return `${productId}:${Math.floor(quantity)}:${Math.floor(days)}`;
                }
                return `${productId}:${Math.floor(quantity)}`;
            })
            .filter(Boolean)
            .join(",");
    }, [normalizedProducts]);
    const [debouncedProductsKey, setDebouncedProductsKey] = useState(productsKey);
    useEffect(() => {
        const timeoutId = window.setTimeout(() => {
            setDebouncedProductsKey(productsKey);
        }, 350);
        return () => window.clearTimeout(timeoutId);
    }, [productsKey]);
    const debouncedStableProducts = useMemo(() => {
        if (!debouncedProductsKey) return EMPTY_PRODUCTS;
        return debouncedProductsKey.split(",").reduce((carry, item) => {
            const [rawId, rawQuantity, rawDays] = item.split(":");
            const productId = Number(rawId);
            const quantity = Number(rawQuantity);
            const days = Number(rawDays);
            if (!Number.isFinite(productId) || productId <= 0) return carry;
            if (!Number.isFinite(quantity) || quantity <= 0) return carry;
            carry[productId] = {
                quantity: Math.floor(quantity),
                days: Number.isFinite(days) && days > 0 ? Math.floor(days) : null
            };
            return carry;
        }, {});
    }, [debouncedProductsKey]);
    const getOrderLineUnitPrice = useCallback((line) => {
        const preferredValue = Number(
            showPricesInclVat ? line?.price_including_vat : line?.price_excluding_vat
        );
        if (Number.isFinite(preferredValue)) return preferredValue;

        const fallbackValue = Number(
            showPricesInclVat ? line?.price_excluding_vat : line?.price_including_vat
        );
        return Number.isFinite(fallbackValue) ? fallbackValue : null;
    }, [showPricesInclVat]);

    const subsidiaryId = useMemo(() => {
        return resolveWhiteLabelSubsidiaryId(vehicleInfo.company.id);
    }, [vehicleInfo]);

    const currentBalance = useMemo(() => {
        if (runtimeBalance !== null && runtimeBalance !== undefined) {
            return Number(runtimeBalance) || 0;
        }
        return Number(activeCustomer?.contract?.current_balance) || 0;
    }, [runtimeBalance, activeCustomer?.contract?.current_balance]);

    const depositRequired = useMemo(() => {
        return Number(reservationPrice?.deposit_required) || 0;
    }, [reservationPrice?.deposit_required]);
    const isPrivateShareP2PRequest = useMemo(() => {
        return getAppliedWhiteLabelThemeId() === "privateshare";
    }, []);
    const shouldSkipP2PRequestCommentModal = useMemo(() => {
        return isPrivateShareP2PRequest && Number(vehicleInfo?.company?.id) === 9;
    }, [isPrivateShareP2PRequest, vehicleInfo?.company?.id]);

    const totalToPay = useMemo(() => {
        if (!activeCustomer || !reservationPrice) return null;
        if (isPrivateShareP2PRequest) return 0;
        return Math.max(0, depositRequired - currentBalance);
    }, [activeCustomer, reservationPrice, isPrivateShareP2PRequest, depositRequired, currentBalance]);

    const orderLines = useMemo(() => {
        if (!Array.isArray(reservationPrice?.order_lines)) return [];
        return reservationPrice.order_lines;
    }, [reservationPrice?.order_lines]);

    const rentOrderLine = useMemo(() => {
        if (!orderLines.length) return null;

        const byType = orderLines.find(({type}) => String(type || "").toLowerCase() === "vehicle_rent");
        if (byType) return byType;

        const byKnownType = orderLines.find(({type}) =>
            ["rent", "base_rent", "hourly_rent", "daily_rent", "reservation_rent"].includes(
                String(type || "").toLowerCase()
            )
        );
        if (byKnownType) return byKnownType;

        return orderLines.find(({description}) => {
            const descriptionText = String(description || "").toLowerCase();
            return descriptionText.includes("huur") || descriptionText.includes("rent");
        }) || null;
    }, [orderLines]);

    const additionalOrderLines = useMemo(() => {
        const companyId = vehicleInfo?.company?.id;
        if (!orderLines.length) return [];
        if (!rentOrderLine) {
            return orderLines.map((line) => ({
                ...line,
                description: getAdditionalOrderLineDescription(line, companyId)
            }));
        }
        return orderLines
            .filter((line) => line !== rentOrderLine)
            .map((line) => ({
                ...line,
                description: getAdditionalOrderLineDescription(line, companyId)
            }));
    }, [orderLines, rentOrderLine, vehicleInfo?.company?.id]);

    const additionalOrderLinesTotal = useMemo(() => {
        if (!additionalOrderLines.length) return 0;
        return additionalOrderLines.reduce((carry, line) => {
            const quantity = Number(line?.quantity) || 1;
            const linePrice = getOrderLineUnitPrice(line);
            if (!Number.isFinite(linePrice)) return carry;
            return carry + (linePrice * quantity);
        }, 0);
    }, [additionalOrderLines, getOrderLineUnitPrice]);

    const freeKilometers = useMemo(() => {
        const fromCalculator = Number(reservationPrice?.free_kilometers);
        if (Number.isFinite(fromCalculator)) return fromCalculator;

        const fromVehicle = Number(vehicleInfo?.prices?.category?.free_km_per_day ?? 0);
        return Number.isFinite(fromVehicle) ? fromVehicle : 0;
    }, [reservationPrice?.free_kilometers, vehicleInfo?.prices?.category?.free_km_per_day]);
    const extraKilometerPrice = useMemo(() => {
        const fromCalculator = Number(reservationPrice?.price_per_extra_kilometer);
        if (Number.isFinite(fromCalculator)) return fromCalculator;

        const fromVehicle = Number(vehicleInfo?.prices?.category?.km ?? 0);
        return Number.isFinite(fromVehicle) ? fromVehicle : 0;
    }, [reservationPrice?.price_per_extra_kilometer, vehicleInfo?.prices?.category?.km]);
    const isDailyRental = String(vehicleInfo?.rental_config?.minimum_rental_time?.unit || "").toLowerCase() === "day";
    const shouldShowFreeKmNote = !isDailyRental && Number.isFinite(freeKilometers) && freeKilometers > 0;
    const hasUnlimitedFreeKm = Number.isFinite(freeKilometers) && freeKilometers >= 100000;

    const rentDurationPrice = useMemo(() => {
        if (rentOrderLine) {
            const quantity = Number(rentOrderLine?.quantity) || 1;
            const linePrice = getOrderLineUnitPrice(rentOrderLine);
            if (Number.isFinite(linePrice)) {
                return linePrice * quantity;
            }
        }

        const totalReservationPrice = Number(
            showPricesInclVat ? reservationPrice?.total_price_including_vat : reservationPrice?.total_price_excluding_vat
        );
        if (!Number.isFinite(totalReservationPrice)) return null;

        const inferredRentPrice = totalReservationPrice - additionalOrderLinesTotal;
        if (!Number.isFinite(inferredRentPrice) || inferredRentPrice < 0) return null;

        return inferredRentPrice;
    }, [
        rentOrderLine,
        reservationPrice?.total_price_including_vat,
        reservationPrice?.total_price_excluding_vat,
        additionalOrderLinesTotal,
        showPricesInclVat,
        getOrderLineUnitPrice
    ]);

    const totalReservationPrice = useMemo(() => {
        const preferredValue = Number(
            showPricesInclVat ? reservationPrice?.total_price_including_vat : reservationPrice?.total_price_excluding_vat
        );
        if (Number.isFinite(preferredValue)) return preferredValue;

        const fallbackValue = Number(
            showPricesInclVat ? reservationPrice?.total_price_excluding_vat : reservationPrice?.total_price_including_vat
        );
        return Number.isFinite(fallbackValue) ? fallbackValue : null;
    }, [reservationPrice?.total_price_including_vat, reservationPrice?.total_price_excluding_vat, showPricesInclVat]);

    useEffect(() => {
        if (typeof onDemoPaymentAmountChange !== "function") return;
        if (showDeposit) {
            onDemoPaymentAmountChange(Number.isFinite(depositRequired) ? depositRequired : null);
            return;
        }
        if (totalReservationPrice === null) {
            onDemoPaymentAmountChange(null);
            return;
        }
        onDemoPaymentAmountChange(totalReservationPrice);
    }, [onDemoPaymentAmountChange, totalReservationPrice, showDeposit, depositRequired]);

    const specialPriceMeta = useMemo(() => {
        const specialPriceRule = rentOrderLine?.special_price_rule;
        const enabledFor = String(specialPriceRule?.enabled_for || "").trim();
        const specialPriceType = isSpecialTariffType(enabledFor)
            ? enabledFor
            : null;
        const isWeekendKb = isWeekendTariff(specialPriceType);
        const isSaturdayKb = isSaturdayTariff(specialPriceType);
        const isHoliday = isHolidayTariff(specialPriceType);
        const holidayTariffName = isHoliday ? getTariffName(specialPriceRule) : "";

        let vehicleRentPrice = null;
        if (rentOrderLine) {
            const quantity = Number(rentOrderLine?.quantity) || 1;
            const linePrice = getOrderLineUnitPrice(rentOrderLine);
            if (Number.isFinite(linePrice)) {
                vehicleRentPrice = linePrice * quantity;
            }
        }

        return {
            specialPriceType,
            enabledFor,
            specialPriceRule,
            isWeekendKb,
            isSaturdayKb,
            isHoliday,
            holidayTariffName,
            vehicleRentPrice: Number.isFinite(vehicleRentPrice) ? vehicleRentPrice : null,
            freeKilometers: Number.isFinite(Number(reservationPrice?.free_kilometers))
                ? Number(reservationPrice?.free_kilometers)
                : null,
            expectedKilometers: Number.isFinite(Number(reservationPrice?.expected_kilometers))
                ? Number(reservationPrice?.expected_kilometers)
                : null,
            pricePerExtraKilometer: Number.isFinite(Number(reservationPrice?.price_per_extra_kilometer))
                ? Number(reservationPrice?.price_per_extra_kilometer)
                : null,
            eigenRisk: Number.isFinite(Number(reservationPrice?.category?.eigen_risk))
                ? Number(reservationPrice?.category?.eigen_risk)
                : null,
        };
    }, [
        rentOrderLine,
        reservationPrice?.free_kilometers,
        reservationPrice?.expected_kilometers,
        reservationPrice?.price_per_extra_kilometer,
        reservationPrice?.category?.eigen_risk,
        getOrderLineUnitPrice
    ]);

    const rentalDurationLabel = useMemo(() => {
        if (specialPriceMeta.isWeekendKb) {
            return "Weekendtarief";
        }
        if (specialPriceMeta.isSaturdayKb) {
            return "Zaterdagtarief";
        }
        if (specialPriceMeta.isHoliday) {
            return specialPriceMeta.holidayTariffName || "Feestdagtarief";
        }
        // if (showMinRentalWarning && minRentalText) {
        //     return `${rentalDuration} (${minRentalText})`;
        // }
        return rentalDuration;
    }, [
        specialPriceMeta.isWeekendKb,
        specialPriceMeta.isSaturdayKb,
        specialPriceMeta.isHoliday,
        specialPriceMeta.holidayTariffName,
        showMinRentalWarning,
        minRentalText,
        rentalDuration
    ]);

    const canProceedWithReservation = Boolean(
        !showLongDailySupportMessage &&
        activeCustomer &&
        customerCanCreateReservation(activeCustomer) &&
        authToken &&
        reservationPrice &&
        !isPriceLoading
    );

    const stopPaymentPolling = useCallback(() => {
        if (!paymentPollingIntervalRef.current) return;
        window.clearInterval(paymentPollingIntervalRef.current);
        paymentPollingIntervalRef.current = null;
    }, []);

    const openPaymentLink = useCallback((url) => {
        if (!url || typeof window === "undefined") return;

        const popup = window.open(url, "_blank", "noopener,noreferrer");
        if (popup && typeof popup.focus === "function") {
            popup.focus();
        }
    }, []);

    const resetPaymentState = useCallback(() => {
        stopPaymentPolling();
        setPaymentSession({id: null, url: ""});
        setPaymentStatus("idle");
        setPaymentError("");
        setPaymentErrorType("");
        setIsCreatingPaymentLink(false);
        setIsPaymentModalOpen(false);
    }, [stopPaymentPolling]);

    const navigateToReservationResult = useCallback((reservationResponse, {resultType = "reservation", vehicleId = null} = {}) => {
        const reservationId = String(getReservationIdentifier(reservationResponse) || "");
        const plannedStartDate = reservationResponse?.planned_start_date || rentalPeriod?.from || "";
        const plannedEndDate = reservationResponse?.planned_end_date || rentalPeriod?.to || "";
        const vehiclePlate = reservationResponse?.vehicle?.license_plate || reservationResponse?.license_plate || "";

        // vehicleId: the equivalent vehicle that was booked instead of vehicleInfo.
        const resolvedVehicleId = vehicleId || vehicleInfo?.id || "0";
        const resolvedLocationId = locationId ||
            (Array.isArray(vehicleInfo?.location) ? vehicleInfo.location[0] : vehicleInfo?.location?.id) ||
            "0";
        const rentalUnit = String(vehicleInfo?.rental_config?.minimum_rental_time?.unit || "");

        const query = new URLSearchParams({
            reservationId,
            from: plannedStartDate,
            to: plannedEndDate,
            plate: vehiclePlate,
            vehicleName: vehicleInfo?.name || "",
            rentalUnit,
            resultType,
        });

        navigate({
            pathname: `/reservation-result/${resolvedVehicleId}/${resolvedLocationId}`,
            search: appendPublicWrapperBaseToSearch(query, location.search).toString(),
        }, {
            replace: true,
            state: {
                reservation: reservationResponse || null,
                vehicleInfo: {
                    id: vehicleId || vehicleInfo?.id,
                    name: vehicleInfo?.name,
                    rentalUnit,
                },
                resultType,
            }
        });
    }, [navigate, location.search, rentalPeriod?.from, rentalPeriod?.to, vehicleInfo, locationId]);

    const createReservation = useCallback(async ({initComment = ""} = {}) => {
        if (!canProceedWithReservation || isCreatingReservation || reservationCreationRef.current) return;

        // CheqM needs a confirmed residence address, so it runs from the address modal before the reservation.
        if (!isPrivateShareP2PRequest && !cheqmDoneRef.current && isCheqmRequired(activeCustomer)) {
            setIsPaymentModalOpen(false);
            setIsCheqmModalOpen(true);
            return;
        }

        reservationCreationRef.current = true;
        setIsCreatingReservation(true);
        setReservationError("");

        const showVehicleUnavailable = (vehicles) => {
            sendEvent("reserve_period_unavailable");
            setIsPaymentModalOpen(false);
            setReservationError(unavailableMessageToText(buildUnavailableMessage(vehicles)));
            reservationCreationRef.current = false;
        };

        try {
            let reservationResponse;
            let bookedVehicleId = String(vehicleInfo.id);

            if (isPrivateShareP2PRequest) {
                reservationResponse = await createP2PReservationRequestAPI({
                    authToken,
                    subsidiaryId,
                    customerId: activeCustomer.id,
                    vehicleId: vehicleInfo.id,
                    startDate: rentalPeriod.from,
                    endDate: rentalPeriod.to,
                    expectedKm: Number(expectedKm) || 0,
                    initComment,
                });
            } else {
                const createFor = (targetVehicleId) => createReservationAPI({
                    authToken,
                    subsidiaryId,
                    customerId: activeCustomer.id,
                    vehicleId: Number(targetVehicleId),
                    startDate: rentalPeriod.from,
                    endDate: rentalPeriod.to,
                    expectedKm: Number(expectedKm) || 0,
                    options: stableOptions,
                });
                const resolveFor = (targetVehicleId) => resolveBookableVehicle({
                    locationId,
                    vehicleId: targetVehicleId,
                    startDate: rentalPeriod.from,
                    endDate: rentalPeriod.to,
                });

                // Check again right before booking: the vehicle may have been booked since the period step.
                const bookable = await resolveFor(bookedVehicleId);
                if (bookable.status === "unavailable") {
                    showVehicleUnavailable(bookable.vehicles);
                    return;
                }
                if (bookable.status === "swapped") {
                    sendEvent("reserve_vehicle_swapped");
                    bookedVehicleId = bookable.vehicleId;
                }

                try {
                    reservationResponse = await createFor(bookedVehicleId);
                } catch (error) {
                    if (!isVehicleNotAvailableError(error)) throw error;

                    // Booked between the check and this request: one more check, then a free equivalent vehicle.
                    const retry = await resolveFor(bookedVehicleId);
                    if (retry.status === "unavailable") {
                        showVehicleUnavailable(retry.vehicles);
                        return;
                    }
                    if (retry.status !== "swapped") throw error;

                    sendEvent("reserve_vehicle_swapped");
                    bookedVehicleId = retry.vehicleId;
                    reservationResponse = await createFor(bookedVehicleId);
                }
            }

            sendEvent("reserve_booking_confirmed");

            setIsP2PRequestCommentModalOpen(false);
            navigateToReservationResult(reservationResponse, {
                resultType: isPrivateShareP2PRequest ? "p2p_request" : "reservation",
                vehicleId: bookedVehicleId,
            });
        } catch (error) {
            const checkKey = error?.apiError?.message;
            if (!isPrivateShareP2PRequest && checkKey === "reservation_check.cheqm_required") {
                cheqmDoneRef.current = false;
                reservationCreationRef.current = false;
                setIsPaymentModalOpen(false);
                setIsCheqmModalOpen(true);
                return;
            }
            if (!isPrivateShareP2PRequest && (
                checkKey === "reservation_check.rdw_rejected" ||
                checkKey === "reservation_check.rdw_failed" ||
                error?.status === 401
            )) {
                reservationCreationRef.current = false;
                setIsPaymentModalOpen(false);
                setReservationCheckProblem(checkKey === "reservation_check.rdw_failed" ? "failed" : "rejected");
                if (typeof onCustomerChanged === "function") onCustomerChanged();
                return;
            }

            setReservationError(
                isPrivateShareP2PRequest
                    ? "Aanvraag versturen is mislukt. Probeer opnieuw."
                    : "Reservering maken is mislukt. Probeer opnieuw."
            );
            reservationCreationRef.current = false;
        } finally {
            setIsCreatingReservation(false);
        }
    }, [
        canProceedWithReservation,
        isCreatingReservation,
        authToken,
        subsidiaryId,
        vehicleInfo?.id,
        rentalPeriod?.from,
        rentalPeriod?.to,
        expectedKm,
        stableOptions,
        isPrivateShareP2PRequest,
        navigateToReservationResult,
        activeCustomer,
        onCustomerChanged,
        locationId
    ]);

    const onCheqmDone = useCallback((customers) => {
        setIsCheqmModalOpen(false);
        if (typeof onCustomerChanged === "function") onCustomerChanged();

        const customer = Array.isArray(customers) ? customers.find(({id}) => id === activeCustomer?.id) : null;
        if (!customer) {
            setReservationCheckProblem("failed");
            return;
        }
        // A rejected or failed CheqM puts the customer back to review. check_result.message shows the first problem
        // only (e.g. the contract), so compare acceptance before and after; the backend gives the final word.
        const wasAccepted = activeCustomer?.check_result?.accepted === true;
        const isAccepted = customer?.check_result?.accepted === true;
        if ((wasAccepted && !isAccepted) || customer?.check_result?.message === "rentPermissionCheckResult.invalidScreening") {
            setReservationCheckProblem("rejected");
            return;
        }
        if (isCheqmRequired(customer)) {
            setReservationCheckProblem("failed");
            return;
        }

        cheqmDoneRef.current = true;
        void createReservation();
    }, [activeCustomer, onCustomerChanged, createReservation]);

    const onReservationActionClick = useCallback(() => {
        if (isPrivateShareP2PRequest) {
            if (shouldSkipP2PRequestCommentModal) {
                void createReservation();
                return;
            }

            setP2PRequestComment("");
            setIsP2PRequestCommentModalOpen(true);
            return;
        }

        void createReservation();
    }, [isPrivateShareP2PRequest, shouldSkipP2PRequestCommentModal, createReservation]);

    const submitP2PRequestComment = useCallback(() => {
        void createReservation({initComment: p2pRequestComment});
    }, [createReservation, p2pRequestComment]);

    const refreshCustomerBalance = useCallback(async () => {
        if (!authToken || !subsidiaryId || !activeCustomer?.id) return null;

        const customers = await getCustomersAPI({authToken, subsidiaryId});
        const preferredCustomer = findPreferredCustomer(customers, activeCustomer.id);
        if (!preferredCustomer) return null;

        const nextBalance = Number(preferredCustomer?.contract?.current_balance);
        if (Number.isFinite(nextBalance)) {
            setRuntimeBalance(nextBalance);
        }

        return preferredCustomer;
    }, [authToken, subsidiaryId, activeCustomer?.id]);

    const pollPaymentStatus = useCallback(async (paymentIdValue) => {
        if (!authToken || !activeCustomer?.id || !reservationPrice) return;
        if (reservationCreationRef.current) return;

        let latestBalance = Number(currentBalance) || 0;

        try {
            const refreshedCustomer = await refreshCustomerBalance();
            if (refreshedCustomer) {
                latestBalance = Number(refreshedCustomer?.contract?.current_balance) || latestBalance;
            }
        } catch (error) {
            // Ignore balance refresh errors and continue with transaction status check.
        }

        if (latestBalance >= depositRequired) {
            stopPaymentPolling();
            setPaymentStatus("accepted");
            sendEvent("reserve_payment_accepted");
            await createReservation();
            return;
        }

        if (!paymentIdValue) return;

        try {
            const transactionResponse = await getCustomerTransactionDetailAPI({
                authToken,
                subsidiaryId,
                customerId: activeCustomer.id,
                paymentId: paymentIdValue,
            });

            const status = String(transactionResponse?.status || "").toLowerCase();
            if (status === "accepted") {
                stopPaymentPolling();
                setPaymentStatus("accepted");
                sendEvent("reserve_payment_accepted");
                await createReservation();
                return;
            }

            if (["rejected", "revoked", "expired"].includes(status)) {
                stopPaymentPolling();
                setPaymentStatus("rejected");
                sendEvent(`reserve_payment_${status}`);
                setPaymentError("Betaling is niet gelukt. Probeer opnieuw.");
            }
        } catch (error) {
            // Keep polling, transaction endpoint may be temporarily unavailable.
        }
    }, [
        authToken,
        activeCustomer?.id,
        reservationPrice,
        currentBalance,
        refreshCustomerBalance,
        depositRequired,
        stopPaymentPolling,
        subsidiaryId,
        createReservation
    ]);

    const startPaymentPolling = useCallback((paymentIdValue) => {
        stopPaymentPolling();
        void pollPaymentStatus(paymentIdValue);
        paymentPollingIntervalRef.current = window.setInterval(() => {
            void pollPaymentStatus(paymentIdValue);
        }, 5000);
    }, [pollPaymentStatus, stopPaymentPolling]);

    const onPayClick = useCallback(async () => {
        if (!canProceedWithReservation || isCreatingPaymentLink || isCreatingReservation) return;
        if (!activeCustomer?.id || !authToken || totalToPay === null || totalToPay <= 0) return;

        setReservationError("");

        // Do not take a payment when no equivalent vehicle is free any more; a swap happens when booking.
        if (!isPrivateShareP2PRequest) {
            setIsCreatingPaymentLink(true);
            const bookable = await resolveBookableVehicle({
                locationId,
                vehicleId: vehicleInfo?.id,
                startDate: rentalPeriod?.from,
                endDate: rentalPeriod?.to,
            });
            if (bookable.status === "unavailable") {
                setIsCreatingPaymentLink(false);
                sendEvent("reserve_period_unavailable");
                setReservationError(unavailableMessageToText(buildUnavailableMessage(bookable.vehicles)));
                return;
            }
        }

        setPaymentError("");
        setPaymentErrorType("");
        setIsPaymentModalOpen(true);
        setPaymentStatus("creating");
        setIsCreatingPaymentLink(true);

        try {
            const response = await createPaymentAPI({
                authToken,
                subsidiaryId,
                customerId: activeCustomer.id,
                amount: totalToPay
            });

            if (!response?.url) {
                throw new Error("no_payment_url");
            }

            setPaymentSession({
                id: response.id || null,
                url: response.url
            });
            setPaymentStatus("pending");
            sendEvent("reserve_payment_start");
            startPaymentPolling(response.id || null);
        } catch (error) {
            setPaymentStatus("error");
            if (Number(error?.status) === 409) {
                setPaymentErrorType("rate_limit");
                setPaymentError("Er is onlangs al een betaallink aangemaakt.");
            } else {
                setPaymentErrorType("");
                setPaymentError("Betaallink kon niet worden aangemaakt. Probeer opnieuw.");
            }
        } finally {
            setIsCreatingPaymentLink(false);
        }
    }, [
        canProceedWithReservation,
        isCreatingPaymentLink,
        isCreatingReservation,
        activeCustomer?.id,
        authToken,
        totalToPay,
        subsidiaryId,
        startPaymentPolling,
        isPrivateShareP2PRequest,
        locationId,
        vehicleInfo?.id,
        rentalPeriod?.from,
        rentalPeriod?.to
    ]);

    useEffect(() => {
        const nextBalance = Number(activeCustomer?.contract?.current_balance);
        if (!Number.isFinite(nextBalance)) return;
        setRuntimeBalance(nextBalance);
    }, [activeCustomer?.id, activeCustomer?.contract?.current_balance]);

    useEffect(() => {
        if (showLongDailySupportMessage) {
            setReservationPrice(null);
            setIsPriceLoading(false);
            return;
        }
        if (!optionsReady) return;
        if (rentalPeriod.from && rentalPeriod.to && vehicleInfo?.id) {
            const requestId = requestIdRef.current + 1;
            requestIdRef.current = requestId;

            (async () => {
                setIsPriceLoading(true);
                try {
                    const calculatorResponse = await calculateRentPriceAPI({
                        startDate: rentalPeriod.from,
                        endDate: rentalPeriod.to,
                        vehicleId: vehicleInfo.id,
                        expectedKm: Number(expectedKm) || 0,
                        options: stableOptions,
                        products: debouncedStableProducts
                    });

                    if (requestIdRef.current === requestId) {
                        setReservationPrice(calculatorResponse);
                    }
                } finally {
                    if (requestIdRef.current === requestId) {
                        setIsPriceLoading(false);
                    }
                }
            })();
        }
    }, [rentalPeriod.from, rentalPeriod.to, vehicleInfo?.id, optionsKey, debouncedProductsKey, optionsReady, expectedKm, stableOptions, debouncedStableProducts, showLongDailySupportMessage]);

    useEffect(() => {
        if (!onPricingMetaChange) return;
        onPricingMetaChange(specialPriceMeta);
    }, [onPricingMetaChange, specialPriceMeta]);

    useEffect(() => {
        if (!onPriceLoadingChange) return;
        onPriceLoadingChange(isPriceLoading);
    }, [onPriceLoadingChange, isPriceLoading]);

    useEffect(() => {
        if (!isInfoOpen && !isImagePreviewOpen) return;
        const onKeyDown = (event) => {
            if (event.key === "Escape") {
                setIsInfoOpen(false);
                setIsImagePreviewOpen(false);
            }
        };
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [isInfoOpen, isImagePreviewOpen]);

    useEffect(() => {
        return () => stopPaymentPolling();
    }, [stopPaymentPolling]);

    return (
        <div className="kav-summary-wrap">
            <div className="kav-summary">
                <div className="kav-summary__header">
                    <div className="kav-summary__titleWrap">
                        <h2 className="kav-summary__title">Overzicht van je bestelling</h2>
                        <div className="kav-summary__vehicle">
                            <div className="kav-summary__vehicleTitle">
                                {vehicleInfo.name}
                                <button
                                    type="button"
                                    className="kav-summary__infoBtn"
                                    aria-label={`Meer informatie over ${vehicleInfo.name}`}
                                    onClick={() => setIsInfoOpen(true)}
                                >
                                    <i className="bi bi-info-circle"></i>
                                </button>
                            </div>
                            <div className="kav-summary__vehicleSub">{vehicleInfo.model.description}</div>
                        </div>
                    </div>
                    <button
                        type="button"
                        className="kav-summary__imageButton"
                        onClick={() => setIsImagePreviewOpen(true)}
                        aria-label="Open voertuigafbeelding"
                    >
                        <img className="kav-summary__image" src={vehicleInfo.image} alt={vehicleInfo.name || "Voertuig"}/>
                    </button>
                </div>

                <div className="kav-summary__section kav-summary__section--split">
                    <div className="kav-summary__col">
                        <div className="kav-summary__label">Ophalen</div>
                        <div className="kav-summary__date">{pickupDate}</div>
                        <div className="kav-summary__time">{pickupTime}</div>
                    </div>
                    <div className="kav-summary__col">
                        <div className="kav-summary__label">Inleveren</div>
                        <div className="kav-summary__date">{returnDate}</div>
                        <div className="kav-summary__time">{returnTime}</div>
                    </div>
                </div>

                <div className="kav-summary__section">
                    <div className="kav-summary__label">Verhuurvestiging</div>
                    <div className="kav-summary__value">{vehicleInfo.company.name}</div>
                </div>

                <div className="kav-summary__section kav-summary__feature">
                    <span className="kav-summary__featureIcon" aria-hidden="true">
                        {
                            vehicleInfo.model.vehicleType === "bestelbus"
                                ? <i className="bi bi-truck"></i>
                                : <i className="bi bi-car-front"></i>
                        }
                    </span>
                    <span className="kav-summary__featureText">{vehicleInfo.model.vehicleType}</span>
                </div>

                <div className="kav-summary__section kav-summary__feature">
                    <span className="kav-summary__featureIcon" aria-hidden="true">
                        <i className="bi bi-fuel-pump"></i>
                    </span>
                    <span className="kav-summary__featureText">{vehicleInfo.model.fuelType}</span>
                </div>

                <div className="kav-summary__section kav-summary__feature">
                    <span className="kav-summary__featureIcon" aria-hidden="true">
                        <i className="bi bi-gear"></i>
                    </span>
                    <span className="kav-summary__featureText">{vehicleInfo.model.transmission}</span>
                </div>

                {showLongDailySupportMessage ? (
                    <div className="kav-summary__section kav-summary__pricing">
                        <div className="kav-summary__supportLimit kav-callout" style={{background: 'rgb(255, 234, 46)'}}>
                            Voor deze huurperiode is online reserveren niet beschikbaar. Neem contact op met support
                            <br/>
                            <a href="tel:0880883434">088 - 088 34 34</a>
                        </div>
                    </div>
                ) : (
                    <>
                        <div className="kav-summary__section kav-summary__pricing">
                            <div className="kav-summary__priceRow">
                                <span>{rentalDurationLabel}</span>
                                {
                                    isPriceLoading
                                        ? (
                                            <strong className="kav-price-loading">
                                                <span className="kav-spinner" aria-hidden="true"></span>
                                            </strong>
                                        )
                                        : <strong>{rentDurationPrice !== null ? formatPriceNl(rentDurationPrice) : "--"}</strong>
                                }
                            </div>
                            {shouldShowFreeKmNote && (
                                <div className="kav-summary__priceNote">
                                    {hasUnlimitedFreeKm
                                        ? "∞ vrije km"
                                        : `${Math.floor(freeKilometers)} vrije km (EUR ${formatPriceNl(extraKilometerPrice)} per extra km)`}
                                </div>
                            )}
                            {additionalOrderLines.map((line, index) => (
                                <div className="kav-summary__priceRow" key={`${line.type}-${line.description}-${index}`}>
                                    <span>{line.description}{line.quantity > 1 ? ` (${line.quantity})` : ""}</span>
                                    <strong>
                                        {(() => {
                                            const quantity = Number(line?.quantity) || 1;
                                            const linePrice = getOrderLineUnitPrice(line);
                                            if (!Number.isFinite(linePrice)) return "--";
                                            return formatPriceNl(linePrice * quantity);
                                        })()}
                                    </strong>
                                </div>
                            ))}
                        </div>

                        <div className="kav-summary__section kav-summary__total"
                             style={{fontSize: showDeposit ? "16px" : "18px", fontWeight: showDeposit ? 700 : 700}}>
                            <span>{showPricesInclVat ? "Reserveringsprijs incl. BTW" : "Reserveringsprijs excl. BTW"}</span>
                            {
                                totalReservationPrice !== null
                                    ? <strong>{formatPriceNl(totalReservationPrice)}</strong>
                                    : (
                                        <strong className="kav-price-loading">
                                            <span className="kav-spinner" aria-hidden="true"></span>
                                        </strong>
                                    )
                            }
                        </div>

                        {
                            showDeposit
                                ? (
                                    <div className="kav-summary__section kav-summary__total">
                                        <span>Te betalen inclusief de borg</span>
                                        {
                                            reservationPrice
                                                ? <strong>{formatPriceNl(reservationPrice.deposit_required)}</strong>
                                                : (
                                                    <strong className="kav-price-loading">
                                                        <span className="kav-spinner" aria-hidden="true"></span>
                                                    </strong>
                                                )
                                        }
                                    </div>
                                )
                                : ""
                        }
                    </>
                )}

                {
                    !hideActionButtons && activeCustomer && customerCanCreateReservation(activeCustomer) && (
                        <div className="kav-summary__section kav-summary__actions">
                            {
                                demoVirtualCheckout && (
                                    <button
                                        type="button"
                                        onClick={onReservationActionClick}
                                        disabled={isCreatingReservation || !canProceedWithReservation}
                                        className="kav-step-actions__btn kav-step-actions__btn--primary"
                                    >
                                        {isCreatingReservation && <span className="kav-spinner kav-spinner--light" aria-hidden="true"></span>}
                                        {isPrivateShareP2PRequest
                                            ? (isCreatingReservation ? "Aanvraag versturen..." : "Aanvraag versturen")
                                            : (isCreatingReservation ? "Boeking bevestigen..." : "Bevestig je boeking")}
                                    </button>
                                )
                            }
                            {
                                !demoVirtualCheckout && totalToPay === 0 && (
                                    <button
                                        type="button"
                                        onClick={onReservationActionClick}
                                        disabled={isCreatingReservation || !canProceedWithReservation}
                                        className="kav-step-actions__btn kav-step-actions__btn--primary"
                                    >
                                        {isCreatingReservation && <span className="kav-spinner kav-spinner--light" aria-hidden="true"></span>}
                                        {isPrivateShareP2PRequest
                                            ? (isCreatingReservation ? "Aanvraag versturen..." : "Aanvraag versturen")
                                            : (isCreatingReservation ? "Reservering maken..." : "Bevestig je boeking")}
                                    </button>
                                )
                            }
                            {
                                !demoVirtualCheckout && totalToPay > 0 && (
                                    <button
                                        type="button"
                                        onClick={onPayClick}
                                        disabled={!canProceedWithReservation || isCreatingPaymentLink || isCreatingReservation}
                                        className="kav-step-actions__btn kav-step-actions__btn--primary"
                                    >
                                        {(isCreatingPaymentLink || isCreatingReservation) && <span className="kav-spinner kav-spinner--light" aria-hidden="true"></span>}
                                        {isCreatingPaymentLink ? "Betaallink maken..." : `Betalen ${formatPriceNl(totalToPay)}`}
                                    </button>
                                )
                            }
                        </div>
                    )
                }

                {
                    reservationError && (
                        <div className="kav-summary__section">
                            <div className="kav-callout kav-auth-error kav-availability-error">
                                {reservationError}
                            </div>
                        </div>
                    )
                }

                {
                    activeCustomer && !customerCanCreateReservation(activeCustomer) && (
                        <div className="kav-summary__section">
                            <div className="kav-callout kav-auth-status">
                                <span className="kav-spinner" aria-hidden="true"></span>
                                <span>
                                    Je account wordt momenteel gecontroleerd. Zodra je account is goedgekeurd,
                                    kun je direct betalen en reserveren.
                                </span>
                            </div>
                        </div>
                    )
                }
            </div>

            {isInfoOpen && (
                <div className="kav-vehicle-modal" role="dialog" aria-modal="true">
                    <div
                        className="kav-vehicle-modal__backdrop"
                        onClick={() => setIsInfoOpen(false)}
                    />
                    <div className="kav-vehicle-modal__card">
                        <div className="kav-vehicle-modal__header">
                            <div>
                                <h3>{vehicleInfo.name}</h3>
                                <p>{vehicleInfo.model.description}</p>
                            </div>
                            <img src={vehicleInfo.image} alt={vehicleInfo.name || "Voertuig"}/>
                            <button
                                type="button"
                                className="kav-vehicle-modal__close"
                                onClick={() => setIsInfoOpen(false)}
                                aria-label="Sluiten"
                            >
                                <i className="bi bi-x-lg"></i>
                            </button>
                        </div>

                        <div className="kav-vehicle-modal__body">
                            <div className="kav-vehicle-modal__left">
                                <div className="kav-vehicle-modal__features">
                                    {featureItems.map((item) => (
                                        <div key={item.label} className="kav-vehicle-modal__feature">
                                            <i className={item.icon}></i>
                                            <span>{item.label}</span>
                                        </div>
                                    ))}
                                </div>

                                {loadingSpaceSpecs.length > 0 && (
                                    <div className="kav-vehicle-modal__loading-space">
                                        {loadingSpaceSpecs.map((spec, index) => (
                                            <React.Fragment key={`modal-loading-space-${index}`}>
                                                {index > 0 ? <span className="kav-vehicle-modal__loading-separator">|</span> : null}
                                                <span>{spec}</span>
                                            </React.Fragment>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <div className="kav-vehicle-modal__prices">

                                {
                                    vehicleInfo.rental_config.minimum_rental_time.unit === "hour" ?
                                        <>
                                            <div>
                                                <span>Uur</span>
                                                <strong>{formatPriceNl(vehicleInfo.prices.hour)}</strong>
                                            </div>
                                            <div>
                                                <span>Dag</span>
                                                <strong>{formatPriceNl(vehicleInfo.prices.day)}</strong>
                                            </div>
                                            <div>
                                                <span>1 week</span>
                                                <strong>{formatPriceNl(vehicleInfo.prices.week)}</strong>
                                            </div>
                                        </>
                                        :
                                        <>
                                            <div>
                                                <span>Dag</span>
                                                <strong>{formatPriceNl(vehicleInfo.prices.day)}</strong>
                                            </div>
                                            <div>
                                                <span>Weekend</span>
                                                <strong>{formatPriceNl(vehicleInfo.prices.weekend)}</strong>
                                            </div>
                                            <div>
                                                <span>1 week</span>
                                                <strong>{formatPriceNl(vehicleInfo.prices.week)}</strong>
                                            </div>
                                        </>
                                }

                            </div>
                        </div>

                        <div className="kav-vehicle-modal__details">
                            <p>{vehicleInfo.model.description}</p>
                        </div>
                    </div>
                </div>
            )}

            {isImagePreviewOpen && (
                <div className="kav-image-lightbox" role="dialog" aria-modal="true" aria-label="Voertuigafbeelding">
                    <div
                        className="kav-image-lightbox__backdrop"
                        onClick={() => setIsImagePreviewOpen(false)}
                    />
                    <div className="kav-image-lightbox__content">
                        <button
                            type="button"
                            className="kav-image-lightbox__close"
                            onClick={() => setIsImagePreviewOpen(false)}
                            aria-label="Sluiten"
                        >
                            <i className="bi bi-x-lg"></i>
                        </button>
                        <img
                            className="kav-image-lightbox__image"
                            src={vehicleInfo.image}
                            alt={vehicleInfo.name || "Voertuig"}
                        />
                    </div>
                </div>
            )}

            {isCheqmModalOpen && (
                <CheqmAddressModal
                    authToken={authToken}
                    subsidiaryId={subsidiaryId}
                    onDone={onCheqmDone}
                    onClose={() => setIsCheqmModalOpen(false)}
                />
            )}

            {reservationCheckProblem && (
                <div className="kav-simple-modal" role="dialog" aria-modal="true" aria-labelledby="reservation-check-title">
                    <button
                        type="button"
                        className="kav-simple-modal__backdrop"
                        onClick={() => setReservationCheckProblem(null)}
                        aria-label="Sluiten"
                    />
                    <div className="kav-simple-modal__card">
                        <h3 id="reservation-check-title">Je account wordt beoordeeld</h3>
                        <p>
                            {reservationCheckProblem === "failed"
                                ? "We konden de controle nu niet afronden. Bel support, dan activeren we je account:"
                                : "Je account wordt beoordeeld! Dit duurt meestal maximaal 15 minuten. Wil je je account direct laten activeren? Bel dan support:"}
                        </p>
                        <p><a href="tel:0880883434">088 - 088 34 34</a></p>
                        <div className="kav-auth-actions kav-auth-actions--end">
                            <button
                                type="button"
                                className="kav-step-actions__btn kav-step-actions__btn--primary"
                                onClick={() => setReservationCheckProblem(null)}
                            >
                                Sluiten
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {isP2PRequestCommentModalOpen && (
                <div className="kav-simple-modal" role="dialog" aria-modal="true" aria-labelledby="p2p-request-comment-title">
                    <button
                        type="button"
                        className="kav-simple-modal__backdrop"
                        onClick={() => {
                            if (isCreatingReservation) return;
                            setIsP2PRequestCommentModalOpen(false);
                        }}
                        aria-label="Sluiten"
                    />
                    <div className="kav-simple-modal__card kav-p2p-request-modal">
                        <h3 id="p2p-request-comment-title">Aanvraag versturen</h3>
                        <p>
                            Je stuurt een aanvraag naar de eigenaar. Voeg eventueel een kort bericht toe voor de eigenaar.
                        </p>

                        <label className="kav-p2p-request-modal__field" htmlFor="p2p-request-comment">
                            <span>Bericht aan de eigenaar</span>
                            <textarea
                                id="p2p-request-comment"
                                className="kav-p2p-request-modal__textarea"
                                value={p2pRequestComment}
                                onChange={(event) => setP2PRequestComment(event.target.value.slice(0, 200))}
                                maxLength={200}
                                rows={5}
                                placeholder="Bijvoorbeeld: ik hoor graag of de auto beschikbaar is."
                                disabled={isCreatingReservation}
                            />
                            <small>{p2pRequestComment.length}/200</small>
                        </label>

                        <div className="kav-auth-actions kav-auth-actions--end">
                            <button
                                type="button"
                                className="kav-step-actions__btn kav-step-actions__btn--ghost"
                                onClick={() => setIsP2PRequestCommentModalOpen(false)}
                                disabled={isCreatingReservation}
                            >
                                Annuleren
                            </button>
                            <button
                                type="button"
                                className="kav-step-actions__btn kav-step-actions__btn--primary"
                                onClick={submitP2PRequestComment}
                                disabled={isCreatingReservation || !canProceedWithReservation}
                            >
                                {isCreatingReservation && <span className="kav-spinner kav-spinner--light" aria-hidden="true"></span>}
                                {isCreatingReservation ? "Aanvraag versturen..." : "Doorgaan"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {isPaymentModalOpen && (
                <div className="kav-simple-modal" role="dialog" aria-modal="true">
                    <button
                        type="button"
                        className="kav-simple-modal__backdrop"
                        onClick={() => {
                            if (isCreatingReservation) return;
                            resetPaymentState();
                        }}
                        aria-label="Sluiten"
                    />
                    <div className="kav-simple-modal__card kav-payment-modal">
                        <h3>Betaling reservering</h3>
                        {
                            paymentStatus === "creating" && (
                                <p className="kav-payment-modal__status">
                                    <span className="kav-spinner" aria-hidden="true"></span>
                                    Betaallink wordt aangemaakt...
                                </p>
                            )
                        }

                        {
                            paymentStatus === "pending" && (
                                <p>
                                    De betaallink is klaar. Klik op "Ga naar Bunq" om je betaling af te ronden.
                                    Daarna maken we automatisch je reservering.
                                </p>
                            )
                        }

                        {
                            paymentStatus === "accepted" && (
                                <p className="kav-payment-modal__status">
                                    <span className="kav-spinner" aria-hidden="true"></span>
                                    Betaling ontvangen. We maken je reservering...
                                </p>
                            )
                        }

                        {
                            paymentStatus === "rejected" && (
                                <div className="kav-callout kav-auth-error">
                                    {paymentError || "Betaling is niet gelukt. Probeer opnieuw."}
                                </div>
                            )
                        }

                        {
                            paymentStatus === "error" && (
                                <div className="kav-callout kav-auth-error">
                                    {paymentErrorType === "rate_limit" ? (
                                        <>
                                            <p>
                                                Er is onlangs al een betaallink aangemaakt. Rond de betaling af of wacht 2 minuten voordat u een nieuwe betaallink aanvraagt.
                                            </p>
                                            <p>
                                                Hulp nodig? Neem contact op met onze klantenservice:{" "}
                                                <a href="tel:0880883434">088 - 088 34 34</a>
                                            </p>
                                        </>
                                    ) : (
                                        paymentError || "Er is iets misgegaan met het starten van de betaling."
                                    )}
                                </div>
                            )
                        }

                        <div className="kav-auth-actions">
                            {
                                paymentStatus === "pending" && (
                                    <>
                                        <button
                                            type="button"
                                            className="kav-step-actions__btn kav-step-actions__btn--ghost"
                                            onClick={resetPaymentState}
                                        >
                                            Annuleren
                                        </button>
                                        <button
                                            type="button"
                                            className="kav-step-actions__btn kav-step-actions__btn--primary"
                                            onClick={() => openPaymentLink(paymentSession.url)}
                                        >
                                            Ga naar Bunq
                                        </button>
                                    </>
                                )
                            }

                            {
                                (paymentStatus === "rejected" || paymentStatus === "error") && (
                                    <>
                                        <button
                                            type="button"
                                            className="kav-step-actions__btn kav-step-actions__btn--ghost"
                                            onClick={resetPaymentState}
                                        >
                                            Sluiten
                                        </button>
                                        {paymentErrorType !== "rate_limit" ? (
                                            <button
                                                type="button"
                                                className="kav-step-actions__btn kav-step-actions__btn--primary"
                                                onClick={onPayClick}
                                                disabled={isCreatingPaymentLink}
                                            >
                                                {isCreatingPaymentLink && <span className="kav-spinner kav-spinner--light" aria-hidden="true"></span>}
                                                Opnieuw betalen
                                            </button>
                                        ) : null}
                                    </>
                                )
                            }

                            {
                                paymentStatus === "accepted" && (
                                    <button
                                        type="button"
                                        className="kav-step-actions__btn kav-step-actions__btn--ghost"
                                        disabled
                                    >
                                        Bezig met reservering...
                                    </button>
                                )
                            }
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
